"use client";

import {
    createContext,
    type PropsWithChildren,
    useContext,
    useEffect,
    useState,
} from "react";

import { QUIZ_CATEGORIES } from "~/constants/questionnaire";
import {
    getStorageItem,
    removeStorageItem,
    setStorageItem,
} from "~/lib/storage/Storage";

const QUIZ_COMPLETION_QA_KEY = "innerbalance-qa-quiz-completion";

const SUPPORTED_CATEGORIES = new Set<string>([
    "",
    QUIZ_CATEGORIES.FACE_CREAM,
    QUIZ_CATEGORIES.LIBIDA,
    QUIZ_CATEGORIES.NAD,
]);

type QuizCompletionQaApi = {
    enable: () => void;
    reset: () => void;
    isActive: () => boolean;
};

declare global {
    interface Window {
        InnerBalanceQa?: {
            quizCompletion?: QuizCompletionQaApi;
        };
    }
}

const QuizCompletionQaEnabledContext = createContext(false);

const isMarkerActive = () =>
    getStorageItem(QUIZ_COMPLETION_QA_KEY) === "true";

type Props = PropsWithChildren<{
    enabled: boolean;
}>;

export const QuizCompletionQaProvider = ({
    children,
    enabled,
}: Props) => {
    const [isSimulationActive, setIsSimulationActive] = useState(false);

    useEffect(() => {
        if (!enabled) {
            return;
        }

        // eslint-disable-next-line react-hooks/set-state-in-effect
        setIsSimulationActive(isMarkerActive());

        const quizCompletion: QuizCompletionQaApi = {
            enable: () => {
                setStorageItem(QUIZ_COMPLETION_QA_KEY, "true");
                setIsSimulationActive(true);
            },
            reset: () => {
                removeStorageItem(QUIZ_COMPLETION_QA_KEY);
                setIsSimulationActive(false);
            },
            isActive: isMarkerActive,
        };

        window.InnerBalanceQa = {
            ...window.InnerBalanceQa,
            quizCompletion,
        };

        return () => {
            if (window.InnerBalanceQa?.quizCompletion !== quizCompletion) {
                return;
            }

            delete window.InnerBalanceQa.quizCompletion;

            if (Object.keys(window.InnerBalanceQa).length === 0) {
                delete window.InnerBalanceQa;
            }
        };
    }, [enabled]);

    return (
        <QuizCompletionQaEnabledContext value={enabled && isSimulationActive}>
            {children}
        </QuizCompletionQaEnabledContext>
    );
};

export const useQuizCompletionSimulation = (category: string) => {
    const isSimulationActive = useContext(
        QuizCompletionQaEnabledContext
    );

    return isSimulationActive && SUPPORTED_CATEGORIES.has(category);
};

export default QuizCompletionQaProvider;
